/**
 * Runtime API context — the embed-client's libs (voxcpm-client, speak,
 * chat-client, …) call `getEmbedApiContext()` to learn HOW to hit the
 * backend without knowing WHICH caller is running them. The two
 * consumers register their own context at boot:
 *
 *   - admin-ui: auth-aware (Cognito JWT or admin-token, redirects on 401)
 *   - embed-static: anonymous (no auth header, plain throw on 401)
 *
 * This keeps the embed-client free of admin-only auth code while still
 * letting both consumers share the same runtime libs. Replaces the
 * direct `apiUrl` / `apiAuthHeaders` imports from admin-ui that the
 * pre-extraction libs used.
 */

export interface EmbedApiContext {
  /** Resolve a `/api/...`-relative path to the absolute URL (or pass-through for dev proxy). */
  apiUrl(path: string): string;
  /** Auth headers attached to every request. Anonymous embed-static returns `{}`. */
  apiAuthHeaders(): Record<string, string>;
}

let activeContext: EmbedApiContext | null = null;

export function setEmbedApiContext(ctx: EmbedApiContext): void {
  activeContext = ctx;
}

export function getEmbedApiContext(): EmbedApiContext {
  if (!activeContext) {
    throw new Error(
      'EmbedApiContext not initialised — caller must invoke setEmbedApiContext() at boot',
    );
  }
  return activeContext;
}

/** Test-only: reset between unit tests. */
export function _clearEmbedApiContext(): void {
  activeContext = null;
}
